import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { filter, take, tap } from "rxjs";
import { singleton } from "tsyringe";

import SdkAnalyticsEventsFactory from "./analytics/SdkAnalyticsEventsFactory";
import CommonSdk from "./common_sdk/CommonSdk";
import { SDK_MODULES } from "./constants";
import { SdkDynamicModulesManager } from "./dynamic_modules/SdkDynamicModulesManager";
import SdkEvent from "./events/SdkEvent";
import { SdkEventInterface } from "./events/SdkEventInterface";
import { HookDefinition, HookFunction } from "./hooks/HookDefinition";
import { HooksManager } from "./hooks/HooksManager";
import { SdkRegisteredModuleInterface } from "./SdkRegisteredModuleInterface";
import { SdkInitOptions } from "./types";

@singleton()
class Sdk {
    private readonly _emitter: EventTarget;

    public readonly common?: CommonSdk;

    constructor(
        private readonly analyticsEventsCollectorModule: AnalyticsEventsCollectorModule,
        private readonly baseModule: BaseModule,
        private readonly sdkAnalyticsEventsFactory: SdkAnalyticsEventsFactory,
        private readonly hooksManager: HooksManager,
        private readonly sdkDynamicModulesManager: SdkDynamicModulesManager,
        private readonly initApp?: (options: SdkInitOptions) => void
    ) {
        this._emitter = new EventTarget();
    }

    public init(options: SdkInitOptions) {
        if (this.initApp) {
            this.initApp(options);
        }
    }

    public on(type: string, callback: (event: Event) => void) {
        this._emitter.addEventListener(type, callback);
        this.analyticsEventsCollectorModule.exports.eventsCollector.pushEvent(
            this.sdkAnalyticsEventsFactory.createEventSubscribed(type)
        );
    }

    public off(type: string, callback: (event: Event) => void) {
        this._emitter.removeEventListener(type, callback);
        this.analyticsEventsCollectorModule.exports.eventsCollector.pushEvent(
            this.sdkAnalyticsEventsFactory.createEventUnsubscribed(type)
        );
    }

    public runIfModuleReady(
        moduleName: Exclude<SDK_MODULES, SDK_MODULES.COMMON>,
        callback: () => void
    ): void {
        this.sdkDynamicModulesManager.fetch(moduleName).catch((error) => {
            console.error(`Failed to fetch module ${moduleName}:`, error);
        });
        this.baseModule.exports.modulesStatusesProvider
            .selectModuleStatus(moduleName)
            .pipe(
                filter((status) => status === MODULE_STATUS.INITIALIZED),
                take(1),
                tap(callback)
            )
            .subscribe();
    }

    public dispatch(event: SdkEventInterface) {
        this._emitter.dispatchEvent(new SdkEvent(event.type, event));
    }

    public registerModule(
        moduleName: SDK_MODULES,
        module: SdkRegisteredModuleInterface
    ) {
        module.register(this);

        this.baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            moduleName,
            MODULE_STATUS.INITIALIZED
        );

        this.analyticsEventsCollectorModule.exports.eventsCollector.pushEvent(
            this.sdkAnalyticsEventsFactory.createSdkModuleRegistered(moduleName)
        );
    }

    public registerHook<TInput, TResult>(
        definition: HookDefinition<TInput, TResult>,
        hookFunction: HookFunction<TInput, TResult>
    ): void {
        this.hooksManager.registerHook(definition, hookFunction);
    }
}

export default Sdk;
